import type { Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';

type TFinancialConditionFieldStyle = SystemStyleObject<Theme>;

export const financialConditionTableFieldStyles = {
  field: {
    width: '100%',
    minWidth: 0,
  } satisfies TFinancialConditionFieldStyle,
  dateField: {
    width: 168,
    minWidth: 168,
  } satisfies TFinancialConditionFieldStyle,
  balanceLineField: {
    width: 112,
    minWidth: 112,
  } satisfies TFinancialConditionFieldStyle,
  reportLineField: {
    width: 170,
    minWidth: 170,
  } satisfies TFinancialConditionFieldStyle,
} as const;

export const financialConditionGenerationStyles = {
  sectionContainer: (theme: Theme) => ({
    border: `1px solid ${theme.palette.border.subtle}`,
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
  }),
  sectionHeader: (theme: Theme) => ({
    minHeight: 56,
    display: 'flex',
    alignItems: 'center',
    px: theme.spacing(2.5),
    backgroundColor: theme.palette.background.tableHeader,
  }),
  sectionHeaderTitle: (theme: Theme) => ({
    fontSize: '1rem',
    fontWeight: 800,
    color: theme.palette.text.primary,
  }),
  sectionContent: (theme: Theme) => ({
    px: { xs: theme.spacing(1.5), sm: theme.spacing(2.5) },
    py: theme.spacing(2.5),
    '& > .MuiBox-root > h3': { display: 'none' },
  }),
  editableTableContainer: (theme: Theme) => ({
    width: '100%',
    overflowX: 'auto',
    border: `1px solid ${theme.palette.border.subtle}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
  }),
  editableTable: {
    minWidth: 980,
    tableLayout: 'fixed',
  } satisfies SystemStyleObject<Theme>,
  editableTableHeaderCell: (theme: Theme) => ({
    minWidth: 150,
    px: theme.spacing(1.5),
    py: theme.spacing(1.25),
    fontSize: '0.75rem',
    fontWeight: 800,
    lineHeight: 1.3,
    verticalAlign: 'top',
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.tableHeader,
    borderColor: theme.palette.border.subtle,
  }),
  editableTableCell: (theme: Theme) => ({
    minWidth: 150,
    px: theme.spacing(1.5),
    py: theme.spacing(1.5),
    verticalAlign: 'top',
    borderColor: theme.palette.border.subtle,
    '& .MuiInputLabel-root': { display: 'none' },
    '& .MuiInputBase-root': { backgroundColor: theme.palette.common.white },
    '& .MuiFormControl-root': { minWidth: 0 },
  }),
  editableTableActionCell: (theme: Theme) => ({
    width: 48,
    minWidth: 48,
    px: theme.spacing(0.5),
    py: theme.spacing(1.5),
    textAlign: 'center',
    verticalAlign: 'top',
    backgroundColor: theme.palette.common.white,
    borderColor: theme.palette.border.subtle,
  }),
  periodGrid: (theme: Theme) => ({
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      md: 'minmax(0, 1fr) minmax(140px, 0.45fr) minmax(180px, 0.6fr)',
    },
    gap: theme.spacing(2),
    maxWidth: 860,
  }),
  currencyTotalsTable: (theme: Theme) => ({
    overflow: 'hidden',
    border: `1px solid ${theme.palette.border.subtle}`,
    borderRadius: theme.spacing(1),
    '& .MuiTableCell-root': {
      borderColor: theme.palette.border.subtle,
      whiteSpace: 'nowrap',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      fontWeight: 700,
      backgroundColor: theme.palette.background.tableHeader ?? theme.palette.background.paper,
    },
  }),
  financialAssistanceGroup: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
  }),
} as const;
